import { z } from 'zod';
import { PASSWORD_MAX_LENGTH, Role, Username } from './members';

// What the login API (A2) takes and answers, for the API and the web's login screens (A3). Every
// refusal is `{ error: <code> }`: the web turns the code into words from its dictionary (D6).

/** `POST /api/auth/login`: the username as typed (the Username rule lowercases it) and the password. */
export const LoginRequest = z.object({
  username: Username,
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

/**
 * `POST /api/auth/password`: the password the member has now (the one-time one, at the forced
 * change) and the new one. The new one is checked against NewPassword's rule apart, so the answer
 * can say which rule it broke.
 */
export const PasswordChangeRequest = z.object({
  currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  newPassword: z.string(),
});
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequest>;

/** Who is signed in, as login, `GET /api/auth/me` and the password change answer. Never the hash. */
export const SessionMember = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  role: Role,
  /** True until the member replaces a one-time password: the web shows the forced change (A3). */
  mustChangePassword: z.boolean(),
});
export type SessionMember = z.infer<typeof SessionMember>;

export const SessionResponse = z.object({ member: SessionMember });
export type SessionResponse = z.infer<typeof SessionResponse>;

/**
 * The API's refusals, by code (ARCHITECTURE.md → Requests, sessions and roles):
 * - `invalid` (400): the body isn't what the route takes, or the username breaks the Username rule;
 * - `invalid_credentials` (401): login, an unknown username or a wrong password, told apart by nothing;
 * - `unauthenticated` (401): no session, or it ended or expired;
 * - `disabled` (403): the member is disabled, at login with the right password or on any route;
 * - `password_change_required` (403): a one-time password still to replace; only me, the password
 *   change and logout answer;
 * - `wrong_password` (400), `password_rule` (400), `same_password` (400): the password change;
 * - `locked` (429): five failures from one address; `retryAfter` says in how many seconds;
 * - `forbidden` (403): an admin's route (/api/admin/*), to a member who isn't an admin (A4);
 * - `username_taken` (409): the admin's create or edit, with a username another member has;
 * - `own_account` (403): an admin's own account, which the admin API won't demote, disable, remove
 *   or reset (D23);
 * - `last_admin` (409): the change would leave the club without an active admin (D23);
 * - `event_cancelled` (409): an answer to a cancelled event, which takes none (B2; D33);
 * - `event_started` (409): an answer to an event that has started, or "this and the following"
 *   from one (B2; D33);
 * - `too_large` (413), `not_found` (404; with the admin API, an unknown member too; with the
 *   events API, an unknown event), `server_error` (500).
 */
export const API_ERRORS = [
  'invalid',
  'invalid_credentials',
  'unauthenticated',
  'disabled',
  'password_change_required',
  'wrong_password',
  'password_rule',
  'same_password',
  'locked',
  'forbidden',
  'username_taken',
  'own_account',
  'last_admin',
  'event_cancelled',
  'event_started',
  'too_large',
  'not_found',
  'server_error',
] as const;
export const ApiErrorCode = z.enum(API_ERRORS);
export type ApiErrorCode = z.infer<typeof ApiErrorCode>;

export const ApiErrorBody = z.object({
  error: ApiErrorCode,
  /** With `locked`: the seconds until the next try (the Retry-After header says the same). */
  retryAfter: z.number().int().positive().optional(),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;
